import { useState } from "react";
import { Link } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { 
  Menu, X, ChevronDown, Award, Users, Globe, TrendingUp, 
  Shield, FileCheck, BookOpen, CheckCircle2, Mail, Linkedin, Phone,
  Building2, Scale, GraduationCap, BarChart3, Target, Zap, Lock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    state: "",
    subject: "",
    message: ""
  });
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", message: "" });

    try {
      await axios.post(`${API}/contact`, formData);
      setFormStatus({ 
        type: "success", 
        message: "Thank you! Your message has been received. / Obrigado! Sua mensagem foi recebida." 
      });
      setFormData({
        name: "",
        company: "",
        email: "",
        state: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      setFormStatus({ 
        type: "error", 
        message: "Something went wrong. Please try again. / Algo deu errado. Por favor, tente novamente." 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="App bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-btacc-blue z-50 shadow-lg" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex-shrink-0" data-testid="logo">
              <h1 className="text-3xl font-montserrat font-bold text-white">
                BT<span className="text-btacc-gold">ACC</span>
              </h1>
              <p className="text-xs text-btacc-light font-inter -mt-1">Brazil Tax Advisory & Compliance Center</p>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8" data-testid="desktop-nav">
              <button onClick={() => scrollToSection('home')} className="text-sm font-inter text-white hover:text-btacc-gold transition">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-inter text-white hover:text-btacc-gold transition">About</button>
              <button onClick={() => scrollToSection('legacy')} className="text-sm font-inter text-white hover:text-btacc-gold transition">Legacy</button>
              <button onClick={() => scrollToSection('team')} className="text-sm font-inter text-white hover:text-btacc-gold transition">Team</button>
              <button onClick={() => scrollToSection('services')} className="text-sm font-inter text-white hover:text-btacc-gold transition">Services</button>
              <button onClick={() => scrollToSection('advantages')} className="text-sm font-inter text-white hover:text-btacc-gold transition">Advantages</button>
              <Link to="/whitepapers" className="text-sm font-inter text-btacc-gold hover:text-white transition font-semibold" data-testid="nav-whitepapers">Whitepapers</Link>
              <Link to="/careers" className="text-sm font-inter text-white hover:text-btacc-gold transition">Careers</Link>
              <button onClick={() => scrollToSection('contact')} className="bg-btacc-gold text-btacc-blue px-6 py-2.5 rounded-md font-montserrat font-semibold text-sm hover:bg-white transition" data-testid="book-consultation-btn">
                Book a Consultation
              </button>
            </nav>

            {/* Mobile menu button */}
            <button 
              className="lg:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-btacc-secondary border-t border-btacc-blue" data-testid="mobile-nav">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-white hover:text-btacc-gold">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-white hover:text-btacc-gold">About</button>
              <button onClick={() => scrollToSection('legacy')} className="block w-full text-left py-2 text-white hover:text-btacc-gold">Legacy</button>
              <button onClick={() => scrollToSection('team')} className="block w-full text-left py-2 text-white hover:text-btacc-gold">Team</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-white hover:text-btacc-gold">Services</button>
              <button onClick={() => scrollToSection('advantages')} className="block w-full text-left py-2 text-white hover:text-btacc-gold">Advantages</button>
              <Link to="/whitepapers" className="block w-full text-left py-2 text-btacc-gold hover:text-white font-semibold">Whitepapers</Link>
              <Link to="/careers" className="block w-full text-left py-2 text-white hover:text-btacc-gold">Careers</Link>
              <button onClick={() => scrollToSection('contact')} className="w-full bg-btacc-gold text-btacc-blue px-6 py-2.5 rounded-md font-montserrat font-semibold mt-2">
                Book a Consultation
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-bold text-btacc-blue leading-tight mb-6" data-testid="hero-title">
                U.S.-Based Center Specialized in Brazilian Legislation
              </h1>
              <p className="text-lg md:text-xl font-inter text-btacc-graphite mb-4">
                Built on 50+ years of institutional expertise and supported by a technical team of 120+ Brazilian specialists
              </p>
              <p className="text-base md:text-lg font-inter text-btacc-secondary mb-8 italic">
                Centro baseado nos EUA especializado em legislação brasileira. Construído sobre 50+ anos de expertise institucional e apoiado por uma equipe técnica de 120+ especialistas brasileiros.
              </p>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-btacc-blue text-white px-8 py-4 rounded-md font-montserrat font-semibold text-lg hover:bg-btacc-secondary transition inline-flex items-center"
                data-testid="hero-cta-btn"
              >
                Schedule a Consultation
                <ChevronDown className="ml-2" size={20} />
              </button>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1567193729952-23830c0743ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNvcnBvcmF0ZSUyMGJ1c2luZXNzfGVufDB8fHx8MTc2NTU0Njc2OHww&ixlib=rb-4.1.0&q=85"
                alt="BTACC Professional Services"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About BTACC */}
      <section id="about" className="py-20 bg-btacc-light" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-btacc-blue mb-6">
              About BTACC
            </h2>
            <p className="text-lg font-inter text-btacc-graphite mb-4 leading-relaxed">
              BTACC is a U.S.-based advisory center focused exclusively on Brazilian tax, customs, labor, and compliance legislation. We combine the institutional legacy of Aduaneiras and Cenofisco with a modern operational structure designed for American companies operating with Brazil.
            </p>
            <p className="text-base font-inter text-btacc-secondary italic mb-8">
              BTACC é um centro de consultoria baseado nos EUA focado exclusivamente em legislação tributária, aduaneira, trabalhista e de compliance brasileiro. Combinamos o legado institucional da Aduaneiras e Cenofisco com uma estrutura operacional moderna projetada para empresas americanas operando com o Brasil.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-btacc-blue rounded-full mb-4">
                  <Award className="text-white" size={32} />
                </div>
                <h3 className="text-5xl font-montserrat font-bold text-btacc-gold mb-2">50+</h3>
                <p className="text-btacc-graphite font-inter">Years of Experience</p>
                <p className="text-btacc-secondary font-inter text-sm italic">Anos de Experiência</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-btacc-blue rounded-full mb-4">
                  <Users className="text-white" size={32} />
                </div>
                <h3 className="text-5xl font-montserrat font-bold text-btacc-gold mb-2">120+</h3>
                <p className="text-btacc-graphite font-inter">Specialists</p>
                <p className="text-btacc-secondary font-inter text-sm italic">Especialistas</p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-btacc-blue rounded-full mb-4">
                  <Globe className="text-white" size={32} />
                </div>
                <h3 className="text-5xl font-montserrat font-bold text-btacc-gold mb-2">USA</h3>
                <p className="text-btacc-graphite font-inter">Based & Operating</p>
                <p className="text-btacc-secondary font-inter text-sm italic">Baseado e Operando</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Legacy Section */}
      <section id="legacy" className="py-20 bg-white" data-testid="legacy-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Our Legacy: 50+ Years
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-12">
            Nosso Legado: 50+ Anos
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-btacc-light p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <Shield className="text-btacc-gold mr-3" size={32} />
                <h3 className="text-xl font-montserrat font-semibold text-btacc-blue">Regulatory Excellence</h3>
              </div>
              <p className="text-btacc-graphite font-inter mb-2">
                Over five decades of navigating complex Brazilian regulations, ensuring precision and compliance for international operations.
              </p>
              <p className="text-btacc-secondary font-inter text-sm italic">
                Mais de cinco décadas navegando regulamentos brasileiros complexos, garantindo precisão e conformidade para operações internacionais.
              </p>
            </div>

            <div className="bg-btacc-light p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <GraduationCap className="text-btacc-gold mr-3" size={32} />
                <h3 className="text-xl font-montserrat font-semibold text-btacc-blue">Professional Influence</h3>
              </div>
              <p className="text-btacc-graphite font-inter mb-2">
                Trained thousands of professionals and shaped industry standards through executive education programs and certifications.
              </p>
              <p className="text-btacc-secondary font-inter text-sm italic">
                Treinamos milhares de profissionais e moldamos padrões da indústria através de programas de educação executiva e certificações.
              </p>
            </div>

            <div className="bg-btacc-light p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <Building2 className="text-btacc-gold mr-3" size={32} />
                <h3 className="text-xl font-montserrat font-semibold text-btacc-blue">Institutional Credibility</h3>
              </div>
              <p className="text-btacc-graphite font-inter mb-2">
                Trusted by thousands of Brazilian companies and now bringing that proven methodology to the U.S. market.
              </p>
              <p className="text-btacc-secondary font-inter text-sm italic">
                Confiado por milhares de empresas brasileiras e agora trazendo essa metodologia comprovada para o mercado dos EUA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Team */}
      <section id="team" className="py-20 bg-btacc-light" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Technical Team: 120+ Specialists
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-12">
            Equipe Técnica: 120+ Especialistas
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg border-l-4 border-btacc-gold">
              <Scale className="text-btacc-blue mb-3" size={28} />
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-2">Tax Analysts</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-1">Federal and state tax expertise</p>
              <p className="text-btacc-secondary font-inter text-xs italic">Especialistas tributários federais e estaduais</p>
            </div>

            <div className="bg-white p-6 rounded-lg border-l-4 border-btacc-gold">
              <FileCheck className="text-btacc-blue mb-3" size={28} />
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-2">Customs Specialists</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-1">HS/NCM classification and trade compliance</p>
              <p className="text-btacc-secondary font-inter text-xs italic">Classificação HS/NCM e compliance comercial</p>
            </div>

            <div className="bg-white p-6 rounded-lg border-l-4 border-btacc-gold">
              <Users className="text-btacc-blue mb-3" size={28} />
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-2">Labor & Social Security Experts</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-1">Employment law and social contributions</p>
              <p className="text-btacc-secondary font-inter text-xs italic">Direito do trabalho e contribuições sociais</p>
            </div>

            <div className="bg-white p-6 rounded-lg border-l-4 border-btacc-gold">
              <Shield className="text-btacc-blue mb-3" size={28} />
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-2">Regulatory Consultants</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-1">Compliance and special regimes</p>
              <p className="text-btacc-secondary font-inter text-xs italic">Compliance e regimes especiais</p>
            </div>

            <div className="bg-white p-6 rounded-lg border-l-4 border-btacc-gold">
              <BarChart3 className="text-btacc-blue mb-3" size={28} />
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-2">Auditors</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-1">Risk assessment and preventive audits</p>
              <p className="text-btacc-secondary font-inter text-xs italic">Avaliação de risco e auditorias preventivas</p>
            </div>

            <div className="bg-white p-6 rounded-lg border-l-4 border-btacc-gold">
              <BookOpen className="text-btacc-blue mb-3" size={28} />
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-2">Corporate Instructors</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-1">Executive training and certifications</p>
              <p className="text-btacc-secondary font-inter text-xs italic">Treinamento executivo e certificações</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Our Services
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-12">
            Nossos Serviços
          </p>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Brazilian Trade Compliance */}
            <div className="bg-btacc-light p-8 rounded-lg">
              <div className="bg-btacc-blue inline-block p-4 rounded-lg mb-6">
                <Globe className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-montserrat font-bold text-btacc-blue mb-3">
                Brazilian Trade Compliance
              </h3>
              <p className="text-btacc-secondary font-inter text-sm italic mb-4">
                Compliance Comercial Brasileiro
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">HS/NCM classification</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Pre-shipment review</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Customs risk analysis</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Special regimes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Ongoing support</span>
                </li>
              </ul>
            </div>

            {/* Tax & Fiscal Compliance */}
            <div className="bg-btacc-light p-8 rounded-lg">
              <div className="bg-btacc-blue inline-block p-4 rounded-lg mb-6">
                <FileCheck className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-montserrat font-bold text-btacc-blue mb-3">
                Tax & Fiscal Compliance
              </h3>
              <p className="text-btacc-secondary font-inter text-sm italic mb-4">
                Compliance Fiscal e Tributário
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Federal taxes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">ICMS (Value Added Tax)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Digital obligations (SPED, DCTFWeb)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Labor/social security guidance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Audits and preventive compliance</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">TAX REFORM</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">IVA DUAL - CBS / IBS</span>
                </li>
              </ul>
            </div>

            {/* Executive Professional Education */}
            <div className="bg-btacc-light p-8 rounded-lg">
              <div className="bg-btacc-blue inline-block p-4 rounded-lg mb-6">
                <GraduationCap className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-montserrat font-bold text-btacc-blue mb-3">
                Executive Professional Education
              </h3>
              <p className="text-btacc-secondary font-inter text-sm italic mb-4">
                Educação Profissional Executiva
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Workshops</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Certifications</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">In-company training</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="text-btacc-gold mr-2 flex-shrink-0 mt-1" size={18} />
                  <span className="text-btacc-graphite font-inter text-sm">Regulatory update tracks</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* Competitive Advantages */}
      <section id="advantages" className="py-20 bg-btacc-light" data-testid="advantages-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Competitive Advantages
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-12">
            Vantagens Competitivas
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Award className="text-btacc-gold mb-4" size={36} />
              <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mb-3">50+ Years of Experience</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-2">
                Half a century of institutional expertise in Brazilian regulations
              </p>
              <p className="text-btacc-secondary font-inter text-xs italic">
                Meio século de expertise institucional em regulamentos brasileiros
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Users className="text-btacc-gold mb-4" size={36} />
              <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mb-3">120+ Specialists</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-2">
                Multidisciplinary team covering all aspects of Brazilian compliance
              </p>
              <p className="text-btacc-secondary font-inter text-xs italic">
                Equipe multidisciplinar cobrindo todos os aspectos do compliance brasileiro
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Target className="text-btacc-gold mb-4" size={36} />
              <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mb-3">Exclusive Brazilian Focus</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-2">
                Dedicated solely to Brazilian legislation—unmatched depth
              </p>
              <p className="text-btacc-secondary font-inter text-xs italic">
                Dedicado exclusivamente à legislação brasileira—profundidade incomparável
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <TrendingUp className="text-btacc-gold mb-4" size={36} />
              <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mb-3">Proven Methodology</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-2">
                Applied successfully to thousands of Brazilian companies
              </p>
              <p className="text-btacc-secondary font-inter text-xs italic">
                Aplicado com sucesso a milhares de empresas brasileiras
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Zap className="text-btacc-gold mb-4" size={36} />
              <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mb-3">Executive Deliverables</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-2">
                High-quality outputs designed for C-level decision-making
              </p>
              <p className="text-btacc-secondary font-inter text-xs italic">
                Entregas de alta qualidade projetadas para decisões executivas
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <Globe className="text-btacc-gold mb-4" size={36} />
              <h3 className="text-xl font-montserrat font-semibold text-btacc-blue mb-3">Nationwide U.S. Service</h3>
              <p className="text-btacc-graphite font-inter text-sm mb-2">
                Supporting American companies across all 50 states
              </p>
              <p className="text-btacc-secondary font-inter text-xs italic">
                Apoiando empresas americanas em todos os 50 estados
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Economic Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Economic Impact
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-12">
            Impacto Econômico
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="flex items-start space-x-4 p-6 bg-btacc-light rounded-lg">
              <Shield className="text-btacc-gold flex-shrink-0" size={28} />
              <div>
                <h3 className="font-montserrat font-semibold text-btacc-blue mb-1">Reduced Regulatory Risk</h3>
                <p className="text-btacc-graphite font-inter text-sm mb-1">Minimize exposure to Brazilian penalties</p>
                <p className="text-btacc-secondary font-inter text-xs italic">Minimize exposição a penalidades brasileiras</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 bg-btacc-light rounded-lg">
              <CheckCircle2 className="text-btacc-gold flex-shrink-0" size={28} />
              <div>
                <h3 className="font-montserrat font-semibold text-btacc-blue mb-1">Prevention of Fines</h3>
                <p className="text-btacc-graphite font-inter text-sm mb-1">Proactive compliance avoids costly penalties</p>
                <p className="text-btacc-secondary font-inter text-xs italic">Compliance proativo evita multas custosas</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 bg-btacc-light rounded-lg">
              <BarChart3 className="text-btacc-gold flex-shrink-0" size={28} />
              <div>
                <h3 className="font-montserrat font-semibold text-btacc-blue mb-1">Tax Predictability</h3>
                <p className="text-btacc-graphite font-inter text-sm mb-1">Clear understanding of tax obligations</p>
                <p className="text-btacc-secondary font-inter text-xs italic">Compreensão clara das obrigações fiscais</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 bg-btacc-light rounded-lg">
              <Zap className="text-btacc-gold flex-shrink-0" size={28} />
              <div>
                <h3 className="font-montserrat font-semibold text-btacc-blue mb-1">Efficient Operations</h3>
                <p className="text-btacc-graphite font-inter text-sm mb-1">Streamlined processes and faster clearance</p>
                <p className="text-btacc-secondary font-inter text-xs italic">Processos simplificados e liberação mais rápida</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 bg-btacc-light rounded-lg">
              <Target className="text-btacc-gold flex-shrink-0" size={28} />
              <div>
                <h3 className="font-montserrat font-semibold text-btacc-blue mb-1">Better Decision-Making</h3>
                <p className="text-btacc-graphite font-inter text-sm mb-1">Executive-level insights for strategic planning</p>
                <p className="text-btacc-secondary font-inter text-xs italic">Insights executivos para planejamento estratégico</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 bg-btacc-light rounded-lg">
              <TrendingUp className="text-btacc-gold flex-shrink-0" size={28} />
              <div>
                <h3 className="font-montserrat font-semibold text-btacc-blue mb-1">Sustainable Growth</h3>
                <p className="text-btacc-graphite font-inter text-sm mb-1">Build long-term Brazil operations with confidence</p>
                <p className="text-btacc-secondary font-inter text-xs italic">Construa operações no Brasil com confiança</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20 bg-btacc-light" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-btacc-blue text-center mb-4">
            Contact Us
          </h2>
          <p className="text-center text-btacc-secondary font-inter italic mb-12">
            Fale Conosco
          </p>

          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-sm" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-btacc-graphite font-inter font-medium mb-2" htmlFor="name">
                  Name / Nome *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-btacc-light rounded-md focus:outline-none focus:ring-2 focus:ring-btacc-blue"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-btacc-graphite font-inter font-medium mb-2" htmlFor="company">
                  Company / Empresa *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-btacc-light rounded-md focus:outline-none focus:ring-2 focus:ring-btacc-blue"
                  data-testid="contact-company-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-btacc-graphite font-inter font-medium mb-2" htmlFor="email">
                  Corporate Email / E-mail Corporativo *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-btacc-light rounded-md focus:outline-none focus:ring-2 focus:ring-btacc-blue"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-btacc-graphite font-inter font-medium mb-2" htmlFor="state">
                  U.S. State / Estado dos EUA *
                </label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-btacc-light rounded-md focus:outline-none focus:ring-2 focus:ring-btacc-blue"
                  data-testid="contact-state-input"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-btacc-graphite font-inter font-medium mb-2" htmlFor="subject">
                Subject / Assunto *
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-btacc-light rounded-md focus:outline-none focus:ring-2 focus:ring-btacc-blue"
                data-testid="contact-subject-input"
              />
            </div>

            <div className="mb-6">
              <label className="block text-btacc-graphite font-inter font-medium mb-2" htmlFor="message">
                Message / Mensagem *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="5"
                className="w-full px-4 py-3 border border-btacc-light rounded-md focus:outline-none focus:ring-2 focus:ring-btacc-blue"
                data-testid="contact-message-input"
              ></textarea>
            </div>

            {formStatus.message && (
              <div 
                className={`mb-6 p-4 rounded-md ${
                  formStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
                data-testid="form-status-message"
              >
                {formStatus.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-btacc-blue text-white py-4 rounded-md font-montserrat font-semibold text-lg hover:bg-btacc-secondary transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-submit-btn"
            >
              {isSubmitting ? 'Sending... / Enviando...' : 'Send Message / Enviar Mensagem'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-btacc-blue text-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Logo & Tagline */}
            <div>
              <h2 className="text-3xl font-montserrat font-bold mb-2">
                BT<span className="text-btacc-gold">ACC</span>
              </h2>
              <p className="text-sm text-btacc-light font-inter">
                Specialized consulting backed by 50+ years of expertise.
              </p>
              <p className="text-xs text-btacc-light font-inter italic mt-1">
                Consultoria especializada com 50+ anos de expertise.
              </p>
              <Link
                to="/admin"
                aria-label="Admin"
                title="Admin"
                data-testid="footer-admin-link"
                className="inline-flex items-center mt-4 text-btacc-light/30 hover:text-btacc-gold transition"
              >
                <Lock size={14} />
              </Link>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-montserrat font-semibold mb-4">Quick Links</h3>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('about')} className="block text-sm text-btacc-light hover:text-white transition">About</button>
                <button onClick={() => scrollToSection('legacy')} className="block text-sm text-btacc-light hover:text-white transition">Legacy</button>
                <button onClick={() => scrollToSection('team')} className="block text-sm text-btacc-light hover:text-white transition">Team</button>
                <button onClick={() => scrollToSection('services')} className="block text-sm text-btacc-light hover:text-white transition">Services</button>
                <button onClick={() => scrollToSection('advantages')} className="block text-sm text-btacc-light hover:text-white transition">Advantages</button>
                <Link to="/careers" className="block text-sm text-btacc-light hover:text-white transition">Careers</Link>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-montserrat font-semibold mb-4">Contact</h3>
              <div className="space-y-3">
                <div className="flex items-start text-sm text-btacc-light">
                  <Building2 size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p>Brazil Tax Advisory & Compliance Center</p>
                    <p>320 Decker Drive</p>
                    <p>Irving, Texas 75062</p>
                  </div>
                </div>
                <a href="tel:+18445952060" className="flex items-center text-sm text-btacc-light hover:text-white transition">
                  <Phone size={16} className="mr-2" />
                  +1 (844) 595-2060
                </a>
                <a href="mailto:contact@btacc.info" className="flex items-center text-sm text-btacc-light hover:text-white transition">
                  <Mail size={16} className="mr-2" />
                  contact@btacc.info
                </a>
                <a href="https://www.linkedin.com/company/brazil-tax-advisory-compliance-center" target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-btacc-light hover:text-white transition">
                  <Linkedin size={16} className="mr-2" />
                  LinkedIn
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-btacc-secondary pt-8 text-center">
            <p className="text-sm text-btacc-light">
              © {new Date().getFullYear()} BTACC — All rights reserved. / Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
